import React, { useState, useEffect } from 'react';
import { updateAdminConfig, getSystemWalletBalances, adminTransferFunds, getWalletTransferLogs } from '../../utils/api';
import Pagination from '../Pagination';
import CopyButton from '../CopyButton';

const Wallets = ({ config, loadConfig, flash, f, BC, SYM, fBC, vBC }) => {
  const [balances, setBalances] = useState(null);
  const [loadingBal, setLoadingBal] = useState(false);
  const [transferForm, setTransferForm] = useState({ from: '', to: '', amount: '' });
  const [transferring, setTransferring] = useState(false);
  const [editWallets, setEditWallets] = useState({ ...config.adminWallets });
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);

  const tr = (a) => a ? `${a.slice(0, 8)}...${a.slice(-6)}` : '—';
  // USDT balance color grade: <100 red · 100–500 orange · >500 green
  const usdtColor = (v) => (v == null) ? undefined : (v < 100 ? '#ef4444' : (v <= 500 ? '#f59e0b' : '#16a34a'));
  const LOG_PER = 10;
  const [logPage, setLogPage] = useState(1);
  const [logPages, setLogPages] = useState(1);
  const [logTotal, setLogTotal] = useState(0);

  const walletLabels = [
    ['owner', 'Main / Owner Wallet', 'Receives 100% first, then auto-splits'],
    ['liquidity', 'Liquidity Wallet', '70% of investment as backing'],
    ['teamIncome', 'Team Income Wallet', '15% — holds BNB for level income claims'],
    ['itFund', 'IT Fund Wallet', `${config.adminDistribution?.itFund || 20}% of admin 10%`],
    ['marketingFund', 'Marketing Wallet', `${config.adminDistribution?.marketingFund || 20}% of admin 10%`],
    ['legalFund', 'Legal Fund Wallet', `${config.adminDistribution?.legalFund || 20}% of admin 10%`],
    ['awardFund', 'Award Fund Wallet', `${config.adminDistribution?.awardFund || 40}% of admin 10%`],
    ['royalty', 'Royalty Wallet', `${config.royaltyPercent}% of investment`],
    ['burn', 'Burn Wallet', `${config.buyBurnPercent}% tokens burned`],
  ];

  const loadBalances = async () => {
    setLoadingBal(true);
    try { const res = await getSystemWalletBalances(); setBalances(res.balances); }
    catch { flash('Failed to load balances', 'error'); }
    setLoadingBal(false);
  };

  const loadLogs = async (status = logFilter, pg = logPage) => {
    setLogsLoading(true);
    try {
      const r = await getWalletTransferLogs(status || '', '', pg);
      setLogs(r.logs || []);
      setLogPages(r.pagination?.pages || 1);
      setLogTotal(r.pagination?.total || 0);
    } catch {}
    setLogsLoading(false);
  };

  useEffect(() => { loadBalances(); }, []);
  useEffect(() => { loadLogs(logFilter, logPage); }, [logFilter, logPage]);

  const handleSaveWallets = async () => {
    try { await updateAdminConfig({ adminWallets: editWallets }); flash('Wallets updated'); loadConfig(); }
    catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
  };

  const handleTransfer = async () => {
    if (!transferForm.from || !transferForm.to || !transferForm.amount) { flash('Fill all fields', 'error'); return; }
    setTransferring(true);
    try {
      const res = await adminTransferFunds(transferForm);
      flash(`Transferred: ${res.txHash || 'success'}`);
      setTransferForm({ from: '', to: '', amount: '' }); loadBalances();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setTransferring(false);
  };

  return (
    <div className="fade-in">
      <h2 className="adm-title">Wallet Management</h2>

      <div className="adm-kpi-grid">
        <div className="adm-kpi card"><span className="adm-kpi-label">Liquidity</span><strong className="adm-kpi-val mono">{vBC(config.liquidity)}</strong></div>
        <div className="adm-kpi card"><span className="adm-kpi-label">Level Fund</span><strong className="adm-kpi-val mono">{vBC(config.levelFund)}</strong></div>
        <div className="adm-kpi card"><span className="adm-kpi-label">Royalty Fund</span><strong className="adm-kpi-val mono">{vBC(config.royaltyFund)}</strong></div>
        <div className="adm-kpi card"><span className="adm-kpi-label">Admin Pool</span><strong className="adm-kpi-val mono">{vBC(config.adminPool || 0)}</strong></div>
        <div className="adm-kpi card"><span className="adm-kpi-label">Admin Retained</span><strong className="adm-kpi-val mono">{vBC(config.adminRetainedFund)}</strong></div>
        <div className="adm-kpi card"><span className="adm-kpi-label">Collection</span><strong className="adm-kpi-val mono">{vBC(config.totalCollection)}</strong></div>
      </div>

      <div className="card adm-sub">
        <div className="adm-wallet-header">
          <h3>Wallet Addresses</h3>
          <button className="btn btn-outline btn-sm" onClick={loadBalances} disabled={loadingBal}>
            {loadingBal ? 'Loading...' : 'Refresh Balances'}
          </button>
        </div>
        <div className="table-wrap"><table className="table">
          <thead><tr><th>Wallet</th><th>Purpose</th><th>Address</th><th>BNB (on-chain)</th><th>USDT (on-chain)</th></tr></thead>
          <tbody>
            {walletLabels.map(([key, label, desc]) => {
              const addr = config.adminWallets?.[key] || '—';
              const bal = balances?.[addr];
              return (
                <tr key={key}>
                  <td><strong>{label}</strong><br /><span className="text-3" style={{ fontSize: 11 }}>{desc}</span></td>
                  <td className="mono" style={{ fontSize: 11 }}>{tr(addr)}</td>
                  <td className="mono" style={{ fontSize: 11 }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{addr}{addr && addr !== '—' && <CopyButton text={addr} title="Copy address" size={12} />}</span></td>
                  <td className="mono">{bal && bal.bnb != null ? `${f(bal.bnb, 6)} BNB` : <span className="text-3">—</span>}</td>
                  <td className="mono" style={{ color: bal ? usdtColor(bal.usdt) : undefined, fontWeight: bal && bal.usdt != null ? 700 : 400 }}>{bal && bal.usdt != null ? `${f(bal.usdt, 2)} USDT` : <span className="text-3">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </div>

      <div className="card adm-sub">
        <h3>Update Wallet Addresses</h3>
        <div className="adm-wallet-edit-grid">
          {walletLabels.map(([key, label]) => (
            <div key={key} className="adm-field"><label>{label}</label>
              <input value={editWallets[key] || ''} onChange={e => setEditWallets({ ...editWallets, [key]: e.target.value })} className="input" placeholder="0x..." style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
            </div>
          ))}
        </div>
        <button className="btn btn-primary adm-mt" onClick={handleSaveWallets}>Save Wallets</button>
      </div>

      <div className="card adm-sub">
        <h3>Transfer Funds</h3>
        <p className="adm-hint">Send {BC} between system wallets using private keys from .env</p>
        <div className="adm-form-grid cols-3">
          <div className="adm-field"><label>From</label>
            <select value={transferForm.from} onChange={e => setTransferForm({ ...transferForm, from: e.target.value })} className="input">
              <option value="">Select...</option>
              <option value="main">Main Wallet</option>
              <option value="team_income">Team Income Wallet</option>
            </select>
          </div>
          <div className="adm-field"><label>To Address</label><input value={transferForm.to} onChange={e => setTransferForm({ ...transferForm, to: e.target.value })} className="input" placeholder="0x..." style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} /></div>
          <div className="adm-field"><label>Amount ({BC})</label><input type="number" value={transferForm.amount} onChange={e => setTransferForm({ ...transferForm, amount: e.target.value })} className="input" placeholder="0.00" /></div>
        </div>
        <button className="btn btn-primary adm-mt" onClick={handleTransfer} disabled={transferring}>{transferring ? 'Transferring...' : `Transfer ${BC}`}</button>
      </div>

      {/* Transfer Logs */}
      <div className="card adm-sub">
        <div className="adm-wallet-header">
          <h3>Transfer Logs</h3>
          <div style={{ display: 'flex', gap: 4 }}>
            {['', 'success', 'failed', 'skipped'].map(s => (
              <button key={s} className={`d-range-btn ${logFilter === s ? 'active' : ''}`} onClick={() => { setLogFilter(s); setLogPage(1); }}>
                {s || 'All'}
              </button>
            ))}
          </div>
        </div>
        {logsLoading ? <div className="page-loading"><div className="spinner" /></div> : logs.length > 0 ? (
          <>
          <div className="table-wrap"><table className="table">
            <thead><tr><th>Date</th><th>Status</th><th>Label</th><th>From</th><th>To</th><th>Amount</th><th>Tx Hash</th><th>Error</th></tr></thead>
            <tbody>{logs.map(l => (
              <tr key={l._id} className={l.status === 'failed' ? 'table-row-error' : ''}>
                <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(l.createdAt).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                <td><span className={`d-status-badge badge-${l.status === 'success' ? 'approved' : l.status === 'failed' ? 'rejected' : 'pending'}`}>{l.status}</span></td>
                <td className="mono" style={{ fontSize: 11 }}>{l.label || '—'}</td>
                <td className="mono" style={{ fontSize: 10 }}>{l.from ? `${l.from.slice(0, 6)}...${l.from.slice(-4)}` : '—'}</td>
                <td className="mono" style={{ fontSize: 10 }}>{l.to ? `${l.to.slice(0, 6)}...${l.to.slice(-4)}` : '—'}</td>
                <td className="mono">{f(l.amount, 8)} BNB</td>
                <td className="mono" style={{ fontSize: 10 }}>{l.txHash ? (l.testMode ? 'TEST' : <a href={`https://bscscan.com/tx/${l.txHash}`} target="_blank" rel="noreferrer">{l.txHash.slice(0, 10)}...</a>) : '—'}</td>
                <td style={{ fontSize: 11, color: 'var(--red)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.error}>{l.error || '—'}</td>
              </tr>
            ))}</tbody>
          </table></div>
          <Pagination page={logPage} pages={logPages} total={logTotal} perPage={LOG_PER} onPage={setLogPage} />
          </>
        ) : <div className="d-empty">No transfer logs yet.</div>}
      </div>
    </div>
  );
};

export default Wallets;